import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useEffect } from 'react';
import { X } from 'lucide-react';
import { Portal } from './Portal';
import { useFocusTrap } from './hooks/useFocusTrap';
import { useScrollLock } from './hooks/useScrollLock';
// ---------------------------------------------------------------------------
// Size map — matches Figma size variant spec
// ---------------------------------------------------------------------------
const SIZE_STYLES = {
    sm: { maxWidth: 400, width: '90vw' },
    md: { maxWidth: 500, width: '90vw' },
    lg: { maxWidth: 700, width: '90vw' },
    xl: { maxWidth: 900, width: '90vw' },
    full: { width: '95vw', height: '95vh', maxWidth: 'none', maxHeight: 'none' },
};
// Inner padding per size (Figma: sm=24px, md/lg/xl/full=32px)
const SIZE_PADDING = {
    sm: 24,
    md: 32,
    lg: 32,
    xl: 32,
    full: 32,
};
// ---------------------------------------------------------------------------
// Overlay backdrop
// ---------------------------------------------------------------------------
const BACKDROP_STYLE = {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
    zIndex: 1000,
    overflowY: 'auto',
};
// ---------------------------------------------------------------------------
// Modal panel
// Figma spec: white bg, 10px radius, layered drop-shadow
// ---------------------------------------------------------------------------
const MODAL_BASE_STYLE = {
    backgroundColor: '#ffffff',
    borderRadius: 10,
    boxShadow: '0px 20px 25px 0px rgba(0,0,0,0.10), 0px 8px 10px 0px rgba(0,0,0,0.10)',
    position: 'relative',
    maxHeight: '90vh',
    overflow: 'auto',
    outline: 'none',
};
// ---------------------------------------------------------------------------
// Animation keyframes (injected once)
// ---------------------------------------------------------------------------
const ANIMATION_ID = 'loci-modal-keyframes';
function injectModalKeyframes() {
    if (typeof document === 'undefined')
        return;
    if (document.getElementById(ANIMATION_ID))
        return;
    const style = document.createElement('style');
    style.id = ANIMATION_ID;
    style.textContent = `
    @keyframes fs-modal-in {
      from { opacity: 0; transform: scale(0.95) translateY(8px); }
      to   { opacity: 1; transform: scale(1)    translateY(0);   }
    }
    @keyframes fs-backdrop-in {
      from { opacity: 0; }
      to   { opacity: 1; }
    }
  `;
    document.head.appendChild(style);
}
if (typeof window !== 'undefined') {
    injectModalKeyframes();
}
// ---------------------------------------------------------------------------
// Modal
// ---------------------------------------------------------------------------
export const Modal = forwardRef(({ open, onClose, size = 'md', closeOnBackdropClick = true, closeOnEscape = true, centered = true, className, style, children, 'aria-label': ariaLabel, 'aria-labelledby': ariaLabelledBy, 'aria-describedby': ariaDescribedBy, }, ref) => {
    const { containerRef } = useFocusTrap({ active: open });
    useScrollLock(open);
    // Escape key handler
    useEffect(() => {
        if (!open || !closeOnEscape)
            return;
        const handle = (e) => {
            if (e.key === 'Escape') {
                e.stopPropagation();
                onClose();
            }
        };
        document.addEventListener('keydown', handle);
        return () => document.removeEventListener('keydown', handle);
    }, [open, closeOnEscape, onClose]);
    if (!open)
        return null;
    const hasAriaLabel = typeof ariaLabel === 'string' && ariaLabel.trim().length > 0;
    const backdropStyle = {
        ...BACKDROP_STYLE,
        alignItems: centered ? 'center' : 'flex-start',
        animation: 'fs-backdrop-in 200ms ease',
    };
    const modalStyle = {
        ...MODAL_BASE_STYLE,
        ...SIZE_STYLES[size],
        ...(size === 'full' ? { maxHeight: 'none' } : {}),
        animation: 'fs-modal-in 250ms cubic-bezier(0.16,1,0.3,1)',
        ...style,
    };
    return (_jsx(Portal, { children: _jsx("div", { style: backdropStyle, onClick: (e) => {
                if (closeOnBackdropClick && e.target === e.currentTarget) {
                    onClose();
                }
            }, "data-testid": "modal-backdrop", children: _jsx("div", { ref: (node) => {
                    // Merge both refs
                    containerRef.current = node;
                    if (typeof ref === 'function') {
                        ref(node);
                    }
                    else if (ref) {
                        ref.current = node;
                    }
                }, className: className, style: modalStyle, tabIndex: -1, role: "dialog", "aria-modal": "true", "aria-label": hasAriaLabel ? ariaLabel : ariaLabelledBy ? undefined : 'Dialog', "aria-labelledby": hasAriaLabel ? undefined : ariaLabelledBy, "aria-describedby": ariaDescribedBy, "data-testid": "modal-panel", children: children }) }) }));
});
Modal.displayName = 'Modal';
export const ModalHeader = forwardRef(({ children, showCloseButton = true, onClose, size = 'md', id, className, style }, ref) => {
    const isSmall = size === 'sm';
    const padding = SIZE_PADDING[size];
    return (_jsxs("div", { ref: ref, className: className, style: {
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            padding: `${padding}px ${padding}px 0 ${padding}px`,
            ...style,
        }, children: [_jsx("div", { id: id, style: {
                    fontSize: isSmall ? 18 : 20,
                    fontWeight: 600,
                    lineHeight: '28px',
                    color: '#111827',
                    letterSpacing: isSmall ? '-0.44px' : '-0.45px',
                    flex: 1,
                    paddingRight: 16,
                }, children: children }), showCloseButton && onClose && (_jsx("button", { type: "button", onClick: onClose, "aria-label": "Close", style: {
                    flexShrink: 0,
                    background: 'none',
                    border: 'none',
                    padding: 2,
                    cursor: 'pointer',
                    color: '#6b7280',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: 4,
                    lineHeight: 0,
                    transition: 'color 150ms ease',
                    minWidth: 44,
                    minHeight: 44,
                }, onMouseEnter: (e) => {
                    e.currentTarget.style.color = '#111827';
                }, onMouseLeave: (e) => {
                    e.currentTarget.style.color = '#6b7280';
                }, onFocus: (e) => {
                    e.currentTarget.style.outline = '2px solid #6c63ff';
                    e.currentTarget.style.outlineOffset = '2px';
                }, onBlur: (e) => {
                    e.currentTarget.style.outline = 'none';
                }, children: _jsx(X, { size: 20, strokeWidth: 2 }) }))] }));
});
ModalHeader.displayName = 'ModalHeader';
export const ModalBody = forwardRef(({ children, scrollable = false, size = 'md', className, style }, ref) => {
    const padding = SIZE_PADDING[size];
    return (_jsx("div", { ref: ref, className: className, style: {
            padding: `${padding}px`,
            fontSize: 14,
            lineHeight: '22px',
            color: '#374151',
            ...(scrollable ? { overflowY: 'auto', flex: 1 } : {}),
            ...style,
        }, children: children }));
});
ModalBody.displayName = 'ModalBody';
export const ModalFooter = forwardRef(({ children, size = 'md', className, style }, ref) => {
    const padding = SIZE_PADDING[size];
    return (_jsx("div", { ref: ref, className: className, style: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 12,
            padding: `0 ${padding}px ${padding}px ${padding}px`,
            ...style,
        }, children: children }));
});
ModalFooter.displayName = 'ModalFooter';
export function ModalDivider({ style }) {
    return (_jsx("hr", { style: {
            margin: 0,
            border: 'none',
            borderTop: '1px solid #e5e7eb',
            ...style,
        } }));
}
